/**
 * SettingsFormContainer
 *
 * Multi-step settings flow for updating an existing testimonial campaign,
 * reusing create-campaign steps with custom navigation and update logic.
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../../../ui/layout/card';
import { AlertCircle } from 'lucide-react';
import { themes } from '../../../create-campaign/utils/campaignThemes';
import { InternalName } from '../../../create-campaign/steps/InternalName';
import { BasicInfo } from '../../../create-campaign/steps/BasicInfo';
import { DesignPage } from '../../../create-campaign/steps/DesignPage';
import { CampaignDetails } from '../../../create-campaign/steps/CampaignDetails';
import { ReviewCampaign } from '../../../create-campaign/steps/ReviewCampaign';
import { UpdateLoadingModal } from './UpdateLoadingModal';
import { put, SERVER_URL } from '../../../../api/core';
import { apiRequest } from '../../../../api/core';
import { useToast } from '../../../ui/notifications/toast-notification.jsx';
import { ErrorMessage } from '../../../create-campaign/components/ErrorMessage';
import { StepNavigation } from '../../../create-campaign/navigation/StepNavigation';
import { auth } from '../../../../firebase/firebase';

// Modified isStepValid function for settings flow (without category and subcategory steps)
const isStepValid = (currentStep, formData, surveyQuestions) => {
  console.log('Validating step:', currentStep, 'with data:', { 
    formData,
    surveyQuestions,
    surveyQuestionsLength: surveyQuestions?.length,
    hasValidQuestion: surveyQuestions?.some(q => q.question && q.question.trim()),
    hasExplainerVideo: formData.hasExplainerVideo && formData.explainerVideo
  });
  
  switch (currentStep) {
    case 0: // Internal Name: require a name
      return Boolean(formData.name?.trim());
    case 1: // Design: always valid since theme has a default value
      return true;
    case 2: // Basic Info: require title and description
      return Boolean(formData.title?.trim() && formData.description?.trim());
    case 3: // Campaign Details: require at least one non-empty survey question or explainer video
      const hasValidSurveyQuestions = Array.isArray(surveyQuestions) && 
                                    surveyQuestions.length > 0 && 
                                    surveyQuestions.some(q => q.question && q.question.trim());
      const hasExplainerVideo = formData.hasExplainerVideo && formData.explainerVideo;
      
      console.log('Campaign Details validation details:', {
        hasValidSurveyQuestions,
        hasExplainerVideo,
        surveyQuestions: surveyQuestions || [],
        surveyQuestionsLength: surveyQuestions?.length,
        hasExplainerVideoFlag: formData.hasExplainerVideo,
        explainerVideoExists: Boolean(formData.explainerVideo)
      });
      
      return hasValidSurveyQuestions || hasExplainerVideo;
    case 4: // Review: always valid
      return true;
    default:
      return false;
  }
};

// Map the original step indices to the new ones (skipping category and subcategory)
const mapStepIndex = (originalIndex) => {
  // Original steps: 0=Internal, 1=Category, 2=Subcategory, 3=Design, 4=BasicInfo, 5=Details, 6=Review
  // New steps: 0=Internal, 1=Design, 2=BasicInfo, 3=Details, 4=Review
  const mapping = {
    0: 0, // Internal Name -> Internal Name
    1: 1, // Category -> Design (skip)
    2: 1, // Subcategory -> Design (skip)
    3: 1, // Design -> Design
    4: 2, // BasicInfo -> BasicInfo
    5: 3, // Details -> Details
    6: 4  // Review -> Review
  };
  return mapping[originalIndex] || 0;
};

export function SettingsFormContainer({
  formData,
  updateFormData,
  clearFormCache,
  surveyQuestions,
  setSurveyQuestions,
  aiGeneratedFields,
  setAiGeneratedFields,
  previewImage,
  setPreviewImage,
  hasExplainerVideo,
  setHasExplainerVideo,
  explainerVideo,
  setExplainerVideo,
  selectedTheme,
  setSelectedTheme,
  gradientColors,
  setGradientColors,
  gradientDirection,
  setGradientDirection,
  hexText,
  setHexText,
  currentStep,
  setCurrentStep,
  handleInputChange,
  handleAddQuestion,
  handleRemoveQuestion,
  handleQuestionChange,
  handleImageChange,
  handleRemoveImage,
  handleNext,
  handlePrevious,
  campaignId,
  namespaceId,
  readOnly = false,
  setIsHelpOpen
}) {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [isAIModalOpen, setIsAIModalOpen] = useState(false);
  const [error, setError] = useState(null);
  const [loadingModal, setLoadingModal] = useState({
    isOpen: false,
    status: 'loading',
    error: null,
    campaignId: null,
    campaignName: '',
    message: 'Updating campaign...',
    videoUploadError: null,
    videoUploadWarning: null
  });

  // Total number of steps (reduced by 2 for category and subcategory)
  const totalSteps = 5; // Instead of 7
  
  // Map the current step to the new step index
  const mappedStep = mapStepIndex(currentStep);
  
  // Calculate the canProceed value based on the current step validation
  const canProceed = isStepValid(mappedStep, {
    ...formData,
    hasExplainerVideo,
    explainerVideo
  }, surveyQuestions);
  
  // Check if we're on the first step
  const isFirstStep = mappedStep === 0;
  
  // Check if we're on the last step
  const isLastStep = mappedStep === totalSteps - 1;
  
  // Determine if the button should be disabled
  const isButtonDisabled = React.useMemo(() => {
    return isSubmitting || !canProceed;
  }, [isSubmitting, canProceed]);

  // Custom navigation handlers to skip category and subcategory steps
  const handleCustomNext = () => {
    // Validate the current step before proceeding
    if (!canProceed) {
      // Show validation error message
      const stepName = ['Internal Name', 'Design', 'Basic Info', 'Campaign Details', 'Review'][mappedStep];
      setError({
        message: `Please complete all required fields for the ${stepName} step before proceeding.`
      });
      return;
    }
    
    // Clear any previous errors
    setError(null);
    
    // Handle step navigation - skipping category and subcategory steps
    if (currentStep === 0) {
      // Skip from Internal Name directly to Design (step 3)
      setCurrentStep(3);
    } else if (currentStep === 5) {
      // From Campaign Details, go to Review (step 6)
      setCurrentStep(6);
    } else if (currentStep < 6) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleCustomPrevious = () => {
    if (currentStep === 3) {
      // Skip from Design directly to Internal Name (step 0)
      setCurrentStep(0);
    } else if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  // Get the correct button text based on current state
  const getButtonText = () => {
    if (isSubmitting) {
      return isLastStep ? 'Updating...' : 'Next...';
    }
    
    if (isLastStep) {
      return 'Update Campaign';
    }
    
    return 'Next';
  };

  // Handle campaign update
  const handleUpdateCampaign = async () => {
    if (readOnly) {
      addToast('You do not have permission to update this campaign', 'error');
      return;
    }

    if (!campaignId) {
      addToast('Campaign ID is required', 'error');
      return;
    }

    if (!namespaceId) {
      addToast('Namespace ID is required', 'error');
      return;
    }

    setIsSubmitting(true);
    setLoadingModal({
      isOpen: true,
      status: 'loading',
      error: null,
      campaignId: campaignId,
      campaignName: formData.name,
      message: 'Updating campaign settings...',
      videoUploadError: null,
      videoUploadWarning: null
    });

    try {
      // Get the authentication token
      const idToken = auth.currentUser ? await auth.currentUser.getIdToken() : null;
      
      console.log('Authentication status:', {
        isAuthenticated: !!auth.currentUser,
        hasToken: !!idToken,
        tokenLength: idToken ? idToken.length : 0,
        tokenPreview: idToken ? `${idToken.substring(0, 10)}...` : 'none'
      });
      
      if (!idToken) {
        throw new Error('Authentication token is required. Please sign in again.');
      }
      
      // Validate token format (should be a JWT)
      if (!idToken.includes('.') || idToken.split('.').length !== 3) {
        console.error('Invalid token format:', idToken);
        throw new Error('Invalid authentication token format. Please sign in again.');
      }
      
      // Check if we have a new image (data URL)
      const hasNewImage = previewImage && previewImage.startsWith('data:');
      const isImageRemoved = previewImage === null;
      
      console.log('Image status:', { hasNewImage, isImageRemoved, previewImage: previewImage ? 'exists' : 'null' });
      
      console.log('🎬 Explainer Video Status BEFORE FormData:', {
        hasExplainerVideo,
        explainerVideo,
        explainerVideoType: typeof explainerVideo,
        explainerVideoLength: explainerVideo?.length
      });
      
      // If we have a new image or the image was removed, we need to use FormData for the request
      if (hasNewImage || isImageRemoved) {
        setLoadingModal(prev => ({
          ...prev,
          message: hasNewImage ? 'Preparing campaign data and new image...' : 'Preparing campaign data and removing image...'
        }));
        
        // Create FormData object
        const formDataObj = new FormData();
        
        // Add campaign data fields
        formDataObj.append('name', formData.name);
        formDataObj.append('title', formData.title);
        formDataObj.append('description', formData.description);
        formDataObj.append('businessName', formData.businessName || '');
        formDataObj.append('website', formData.website || '');
        formDataObj.append('email', formData.email || '');
        formDataObj.append('phone', formData.phone || '');
        formDataObj.append('theme', selectedTheme);
        
        // Add custom colors if theme is custom
        if (selectedTheme === 'custom') {
          const customColors = {
            gradientColors,
            gradientDirection,
            textColor: hexText
          };
          formDataObj.append('customColors', JSON.stringify(customColors));
        } else {
          formDataObj.append('customColors', '');
        }
        
        // Add survey questions - extract just the questions into a simple array
        const questionArray = surveyQuestions.map(q => q.question);
        formDataObj.append('surveyQuestions', JSON.stringify(questionArray));
        
        // Add explainer video data
        formDataObj.append('hasExplainerVideo', hasExplainerVideo);
        if (hasExplainerVideo && explainerVideo) {
          formDataObj.append('explainerVideo', explainerVideo);
          console.log('Adding explainer video to FormData:', explainerVideo);
        } else {
          formDataObj.append('explainerVideo', '');
          console.log('Clearing explainer video in FormData');
        }
        
        // Add namespace ID
        formDataObj.append('namespaceId', namespaceId);
        
        // Handle image: either add new image or set to empty string if removed
        if (hasNewImage) {
          // Convert data URL to Blob and add to FormData
          const blob = await (await fetch(previewImage)).blob();
          formDataObj.append('campaignImage', blob, 'campaign-image.jpg');
        } else if (isImageRemoved) {
          // Set campaignImage to empty string to indicate removal
          formDataObj.append('campaignImage', '');
          console.log('Setting campaignImage to empty string to remove image');
        }
        
        // Make the PUT request with FormData
        const formDataEndpoint = `/campaign/campaigns/${campaignId}?namespaceId=${namespaceId}`;
        const formDataUrl = `${SERVER_URL}${formDataEndpoint}`;
        
        console.log('Making FormData PUT request to:', formDataUrl);
        
        const response = await fetch(formDataUrl, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${idToken}`
          },
          body: formDataObj,
          // Don't set Content-Type header, let the browser set it with the boundary
        });
        
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          const errorMessage = errorData.error || `Failed to update campaign. Server returned ${response.status}`;
          console.error('Error updating campaign:', errorMessage);
          throw new Error(errorMessage);
        }
        
        const updatedCampaign = await response.json();
        
        // Handle video upload if video has changed
        if (hasExplainerVideo && explainerVideo && explainerVideo.startsWith('data:')) {
          setLoadingModal(prev => ({
            ...prev,
            message: 'Uploading explainer video...'
          }));

          // Upload video
          const videoFormData = new FormData();
          const blob = await (await fetch(explainerVideo)).blob();
          videoFormData.append('video', blob, 'explainer-video.mp4');
          videoFormData.append('namespaceId', namespaceId);

          // Get the video upload URL
          const videoEndpoint = `/campaign/campaigns/${campaignId}/explainer-upload-url?namespaceId=${namespaceId}`;
          const videoResponse = await apiRequest(videoEndpoint, {
            method: 'GET'
          });

          if (!videoResponse.ok) {
            throw new Error('Failed to get upload URL for explainer video');
          }
          
          const { uploadUrl } = await videoResponse.json();
          
          // Upload the video directly to the signed URL
          const uploadResponse = await fetch(uploadUrl, {
            method: 'PUT',
            headers: {
              'Content-Type': 'video/mp4'
            },
            body: blob
          });

          if (!uploadResponse.ok) {
            throw new Error('Failed to upload explainer video');
          }
        }
        
        // Show success message
        setLoadingModal({
          isOpen: true,
          status: 'success',
          error: null,
          campaignId: campaignId,
          campaignName: formData.name
        });
        
        // Remove automatic navigation to campaign details
        // User can manually navigate back using the back button
      } else {
        // If no image changes, use the regular JSON PUT request
        // Prepare campaign data for update
        const campaignData = {
          name: formData.name,
          title: formData.title,
          description: formData.description,
          businessName: formData.businessName || '',
          website: formData.website || '',
          email: formData.email || '',
          phone: formData.phone || '',
          theme: selectedTheme,
          customColors: selectedTheme === 'custom' ? JSON.stringify({
            gradientColors,
            gradientDirection,
            textColor: hexText
          }) : '',
          surveyQuestions: surveyQuestions.map(q => q.question),
          hasExplainerVideo: hasExplainerVideo,
          explainerVideo: hasExplainerVideo && explainerVideo ? explainerVideo : '',
          namespaceId: namespaceId
        };
        
        console.log('📡 JSON Update - Adding explainer video data:', {
          hasExplainerVideo,
          explainerVideo: hasExplainerVideo && explainerVideo ? explainerVideo : 'cleared',
          campaignDataKeys: Object.keys(campaignData)
        });

        // Update campaign using PUT request to the correct endpoint
        const response = await put(`/campaign/campaigns/${campaignId}?namespaceId=${namespaceId}`, campaignData);
        
        const updatedCampaign = response;
        
        // Handle video upload if video has changed
        if (hasExplainerVideo && explainerVideo && explainerVideo.startsWith('data:')) {
          setLoadingModal(prev => ({
            ...prev,
            message: 'Uploading explainer video...'
          }));

          // Upload video
          const videoFormData = new FormData();
          const blob = await (await fetch(explainerVideo)).blob();
          videoFormData.append('video', blob, 'explainer-video.mp4');
          videoFormData.append('namespaceId', namespaceId);

          // Get the video upload URL
          const videoEndpoint = `/campaign/campaigns/${campaignId}/explainer-upload-url?namespaceId=${namespaceId}`;
          const videoResponse = await apiRequest(videoEndpoint, {
            method: 'GET'
          });

          if (!videoResponse.ok) {
            throw new Error('Failed to get upload URL for explainer video');
          }
          
          const { uploadUrl } = await videoResponse.json();
          
          // Upload the video directly to the signed URL
          const uploadResponse = await fetch(uploadUrl, {
            method: 'PUT',
            headers: {
              'Content-Type': 'video/mp4'
            },
            body: blob
          });

          if (!uploadResponse.ok) {
            throw new Error('Failed to upload explainer video');
          }
        }
        
        // Show success message
        setLoadingModal({
          isOpen: true,
          status: 'success',
          error: null,
          campaignId: campaignId,
          campaignName: formData.name
        });
        
        // Remove automatic navigation to campaign details
        // User can manually navigate back using the back button
      }
    } catch (error) {
      console.error('Error updating campaign:', error);
      
      let errorMessage = 'Failed to update campaign';
      
      if (error.message.includes('Authentication token')) {
        errorMessage = 'Authentication error: Please sign in again to continue.';
      } else if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
        errorMessage = 'Network error: Please check your internet connection and try again.';
      } else if (error.message.includes('timeout')) {
        errorMessage = 'Request timed out: The server took too long to respond. Please try again.';
      } else {
        errorMessage = error.message || 'Failed to update campaign';
      }
      
      setLoadingModal(prev => ({
        ...prev,
        status: 'error',
        error: errorMessage
      }));
      setError(errorMessage);
      addToast(errorMessage, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Placeholder for save draft functionality
  const handleSaveDraft = () => {
    setIsSavingDraft(true);
    setTimeout(() => {
      addToast('Draft saved successfully', 'success');
      setIsSavingDraft(false);
    }, 1000);
  };

  // Placeholder for template functionality
  const handleUseTemplate = () => {
    addToast('Template functionality is not available in settings mode', 'info');
  };

  return (
    <Card className="shadow-sm">
      <CardContent className="p-6">
        <form onSubmit={(e) => {
          e.preventDefault();
          // The form submission is now handled by the StepNavigation component
          // through the onSubmit prop, so we don't need to handle it here
        }}>
          {currentStep === 0 && (
            <InternalName
              formData={formData}
              handleInputChange={handleInputChange}
              aiGeneratedFields={aiGeneratedFields}
            />
          )}

          {currentStep === 3 && (
            <DesignPage
              selectedTheme={selectedTheme}
              setSelectedTheme={setSelectedTheme}
              gradientColors={gradientColors}
              setGradientColors={setGradientColors}
              gradientDirection={gradientDirection}
              setGradientDirection={setGradientDirection}
              hexText={hexText}
              setHexText={setHexText}
              themes={themes}
            />
          )}

          {currentStep === 4 && (
            <BasicInfo
              formData={formData}
              handleInputChange={handleInputChange}
              aiGeneratedFields={aiGeneratedFields}
              readOnly={readOnly}
              previewImage={previewImage}
              handleImageChange={handleImageChange}
              handleRemoveImage={handleRemoveImage}
            />
          )}

          {currentStep === 5 && (
            <CampaignDetails
              formData={formData}
              handleInputChange={handleInputChange}
              surveyQuestions={surveyQuestions}
              handleAddQuestion={handleAddQuestion}
              handleRemoveQuestion={handleRemoveQuestion}
              handleQuestionChange={handleQuestionChange}
              aiGeneratedFields={aiGeneratedFields}
              hasExplainerVideo={hasExplainerVideo}
              setHasExplainerVideo={setHasExplainerVideo}
              explainerVideo={explainerVideo}
              setExplainerVideo={setExplainerVideo}
              readOnly={readOnly}
              setIsHelpOpen={setIsHelpOpen}
            />
          )}

          {currentStep === 5 && <div style={{ display: 'none' }}>{console.log('SettingsFormContainer: Explainer video props:', { hasExplainerVideo, explainerVideo })}</div>}

          {currentStep === 6 && (
            <ReviewCampaign
              formData={formData}
              surveyQuestions={surveyQuestions}
              selectedTheme={selectedTheme}
              previewImage={previewImage}
              hasExplainerVideo={hasExplainerVideo}
              explainerVideo={explainerVideo}
              themes={themes}
              isUpdateMode={true}
              currentNamespace={namespaceId}
              userPermission={readOnly ? 'readonly' : 'read/write'}
              submitButtonText="Update Campaign"
            />
          )}

          <div className="mt-8">
            <ErrorMessage error={error} />

            <StepNavigation
              currentStep={mappedStep}
              totalSteps={totalSteps}
              onPrevious={handleCustomPrevious}
              onNext={handleCustomNext}
              isSubmitting={isSubmitting}
              isSavingDraft={isSavingDraft}
              handleSaveDraft={handleSaveDraft}
              isEditingDraft={false}
              formData={{
                ...formData,
                hasExplainerVideo,
                explainerVideo
              }}
              surveyQuestions={surveyQuestions}
              setIsAIModalOpen={setIsAIModalOpen}
              onUseTemplate={handleUseTemplate}
              submitButtonText="Update Campaign"
              onSubmit={handleUpdateCampaign}
              isSettingsMode={true}
              canProceed={readOnly ? false : canProceed}
            />
          </div>

          {readOnly && (
            <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-md flex items-start gap-2 text-amber-800 dark:bg-amber-900/30 dark:border-amber-800 dark:text-amber-300">
              <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium">Read-only mode</p>
                <p className="text-xs mt-1">You do not have permission to edit this campaign. Contact an administrator to request edit access.</p>
              </div>
            </div>
          )}
        </form>
      </CardContent>

      <UpdateLoadingModal
        isOpen={loadingModal.isOpen}
        status={loadingModal.status}
        error={loadingModal.error}
        campaignId={loadingModal.campaignId}
        campaignName={loadingModal.campaignName}
        message={loadingModal.message}
        videoUploadError={loadingModal.videoUploadError}
        videoUploadWarning={loadingModal.videoUploadWarning}
        onClose={() => setLoadingModal({ ...loadingModal, isOpen: false })}
      />
    </Card>
  );
} 